import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHostChromeSuppressed } from '../../../hooks/useHostChromeSuppressed';
import useHostCloseHint from '../../../hooks/useHostCloseHint';
import styles from './Modal.module.css';

const clsx = (...args) => args.filter(Boolean).join(' ');

// Open modals, innermost last. Escape only closes the topmost one, so a
// confirm nested inside another dialog doesn't take its parent with it.
const openModals = [];

function Modal({
  open,
  onClose,
  title,
  subtitle,
  // Full-bleed strip between the header and the scrollable body: no body
  // padding, own borders. Added for the generator run (Spec / Generate /
  // Select); optional and inert for every other caller.
  subheader,
  size = 'md',
  children,
  actions,
  actionsAlign = 'equal',
  closeOnOverlay = true,
  closeOnEscape = true,
  showClose = true,
  // While a submit is in flight: ×, overlay and Escape are all inert, so the
  // dialog can't be dismissed mid-save by any route.
  busy = false,
  // Opt-in: below 640px, render as a full-bleed sheet (no overlay gutter, no
  // radius, full height) instead of a centred card. Opt-in rather than a blanket
  // media query because 27 call sites share this primitive and only the
  // data-dense ones — where the gutter costs a readable column — want it.
  mobileFullScreen = false,
  // Opt-in: while open, ask the iOS host to stow its bottom-left chrome — the
  // Command Center logo button and any tutorial coach-mark anchored to it, both
  // drawn in a native window OVER the webview. Opt-in for the same reason as
  // mobileFullScreen: only the full-attention tool windows want it, not the
  // confirm dialogs. No-ops off iOS.
  suppressHostChrome = false,
  // Opt-in: this window is a STEP on the way to a tool, not the tool. The host
  // uses it to tell a tutorial lesson apart from the thing it sent you to, so
  // the lesson does not talk over a chooser.
  //
  // It no longer gates the ✕ pulse — `hostCloseHint` does, and the three
  // choosers simply never declare it. So "a chooser never pulses" is now a
  // property of the callers rather than something this component enforces.
  isChooser = false,
  // Opt-in: the host may ask the user to close THIS window, and we draw the
  // attention because its coach marks are native and cannot point at a ✕
  // inside a WebView.
  //
  // Its OWN prop rather than a consequence of `suppressHostChrome`, because
  // the two are no longer the same question. `suppressHostChrome` tells an
  // iOS host to stow its logo — and an ALREADY-SHIPPED host reads that as
  // "the user is busy, stop teaching", which silences the tour over a window
  // whose only teaching IS the pill. So the windows that must keep the tour
  // alive stopped declaring it, and would have lost the pulse with it.
  hostCloseHint = false,
  // Opt-in: draw attention to the × because something outside this dialog is
  // telling the user to close it. Added for the iOS tutorial, whose coach
  // marks are native and so cannot point at anything inside a web modal.
  // Inert for every other caller.
  pulseClose = false,
  // Opt-in: render above `.slide-in-panel-container` (--z-panel), which sits
  // above the --z-modal layer by design so a side panel can cover the group
  // editor's shells. A dialog opened FROM inside such a panel has to beat it,
  // and at phone width — where the panel is full-bleed — it is otherwise
  // invisible entirely, with the panel swallowing the taps meant for it.
  overSlideInPanel = false,
}) {
  const containerRef = useRef(null);
  const tokenRef = useRef({});
  const titleId = useId();
  const canCloseOnEscape = closeOnEscape && !busy;
  const canCloseOnOverlay = closeOnOverlay && !busy;

  // A modal declaring `suppressHostChrome` IS the tool window a lesson narrates,
  // or the chooser on the way to one — never furniture. This is the one caller
  // that names a kind other than the default, which is what keeps the minimap
  // and the elements tray from being mistaken for either.
  useHostChromeSuppressed(open && suppressHostChrome, {
    kind: isChooser ? 'chooser' : 'tool',
  });

  // Gated on the window's own opt-in, no longer on a flag that doubles as
  // "shipped hosts, go quiet".
  //
  // The EXPRESSION is equivalent to the old `suppressHostChrome && !isChooser`
  // for all four windows that received the pulse. The delivered behaviour is
  // not, for two of them: the host computes `closeHint` from its own idea of
  // which window is open, and for Schedules & Takeoffs and Renderings that
  // half ships with the app rather than with this file. On an installed app
  // those two will not pulse until it updates — which is what they already do
  // today, so nothing regresses; it is simply not the no-op "equivalent"
  // suggests on its own.
  const hostWantsThisClosed = useHostCloseHint(open && hostCloseHint);

  useEffect(() => {
    if (!open) return undefined;
    const token = tokenRef.current;
    openModals.push(token);
    return () => {
      const i = openModals.indexOf(token);
      if (i >= 0) openModals.splice(i, 1);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !canCloseOnEscape) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Only the topmost open modal handles Escape.
      if (openModals[openModals.length - 1] !== tokenRef.current) return;
      onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, canCloseOnEscape, onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Save and restore focus across the modal's lifecycle
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement;
    return () => {
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  // Trap Tab navigation within the modal
  useEffect(() => {
    if (!open) return;
    const modalEl = containerRef.current;
    if (!modalEl) return;

    const focusableSelectors =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const focusables = modalEl.querySelectorAll(focusableSelectors);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    modalEl.addEventListener('keydown', handleKeyDown);
    return () => modalEl.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!open) return null;

  const node = (
    <div
      className={clsx(
        styles.overlay,
        mobileFullScreen && styles.overlayFull,
        overSlideInPanel && styles.overlayOverPanel
      )}
      onMouseDown={(e) => {
        if (canCloseOnOverlay && e.target === e.currentTarget) onClose?.();
      }}
      role="presentation"
    >
      <div
        ref={containerRef}
        className={clsx(
          styles.container,
          styles[size],
          mobileFullScreen && styles.containerFull
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
      >
        {(title || showClose) && (
          <div className={styles.header}>
            <div>
              {title && (
                <h2 id={titleId} className={styles.title}>
                  {title}
                </h2>
              )}
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
            {showClose && (
              <button
                type="button"
                className={`${styles.close}${
                  pulseClose || hostWantsThisClosed
                    ? ` ${styles.closePulse}`
                    : ''
                }`}
                onClick={onClose}
                disabled={busy}
                aria-label="Close"
              >
                ×
              </button>
            )}
          </div>
        )}
        {subheader && <div className={styles.subheader}>{subheader}</div>}
        {children !== undefined && (
          <div className={styles.body}>{children}</div>
        )}
        {actions && (
          <div className={clsx(styles.actions, styles[actionsAlign])}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

export default Modal;
